if (!customElements.get("page-header-logo-banner")) {
  customElements.define(
    "page-header-logo-banner",
    class PageHeaderLogoBannerSection extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
        this.video = this.querySelector(".wt-video__movie video");
        this.lowBatteryClass = "low-battery-mode";
        this.isLowBattery = false;
        this.isMobile = window.matchMedia("(max-width: 899px)").matches;
        this.elementToScale = this.querySelector(".wt-logo-banner__logo");

        this.scrollListener = null;
        this.readyClass = "wt-logo-banner--ready";

        // Class added to <body> once the hero logo has scrolled out of view,
        // so the small logo living inside #CustomHeader .header-logo-dock
        // can crossfade in (see .header-logo-dock rules in the header CSS).
        this.dockedBodyClass = "hero-logo-docked";

        // Tracks dock state with hysteresis (see handleLogoSize) so the
        // class doesn't flap on/off when scroll sits right at the boundary.
        this.isDocked = false;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent,
        );

        if (this.scrollListener) {
          document.removeEventListener("scroll", this.scrollListener);
          this.scrollListener = null;
        }

        document.body.classList.remove(this.dockedBodyClass);
      }

      handleShopifyEditorEvent() {
        this.reinit();
      }

      // ---- Header helpers -------------------------------------------------
      // Your header section renders as <div id="CustomHeader"> (see the
      // "Custom Header" section snippet), not <header>, so we look it up by id.
      getHeader() {
        return document.getElementById("CustomHeader");
      }

      getNominalWidth() {
        const header = this.getHeader();
        if (!header) return 160;

        const computedStyle = window.getComputedStyle(header);

        // --logo-width is the *responsive* header logo width (changes per
        // breakpoint). --logo-width-desk is only ever set once, at :root,
        // so it always represents the base "docked" logo width regardless
        // of viewport — that's the target size we scale the hero logo down to.
        const nominalWidthMobile = computedStyle
          .getPropertyValue("--logo-width")
          .trim();
        const nominalWidthDesk = computedStyle
          .getPropertyValue("--hero-logo-width")
          .trim();

        const width = parseInt(this.isMobile ? nominalWidthMobile : nominalWidthDesk);
        return Number.isFinite(width) && width > 0 ? width : 160;
      }

      calculateMaxScale() {
        const nominalWidth = this.getNominalWidth();
        const screenWidth = window.innerWidth;
        const maxLogoWidth = screenWidth * 0.7;
        const maxScale = maxLogoWidth / nominalWidth;
        return Math.min(maxScale, 6);
      }

     setInitialScale() {
  // Start with the original logo size (1400px image)
  this.elementToScale.style.transform = "scale(1)";
}

      handleLogoSize() {
        const logoBanner = this.logoBanner;
        const elementToScale = this.elementToScale;
        const logoWrapper = this.logoBanner.querySelector(
          ".wt-logo-banner__picture",
        );
        const containerBottom = logoBanner.getBoundingClientRect().bottom;
const visibilityRatio = containerBottom / window.innerHeight;
const logoWrapperHeight = Math.max(0, containerBottom);
const vanishingClass = "wt-logo-banner--vanishing";
const inactiveClass = "inactive";

// Logo starts at original size (scale:1)
// and smoothly shrinks while scrolling.
const minScale = 0.25; // Change this if you want the final logo larger/smaller

const progress = Math.max(
  0,
  Math.min(1, containerBottom / window.innerHeight)
);

const scale = minScale + (1 - minScale) * progress;

        logoBanner.classList.toggle(vanishingClass, visibilityRatio < 0.7);

        window.requestAnimationFrame(() => {
          logoWrapper.style.height = `${logoWrapperHeight}px`;
          elementToScale.style.transform = `scale(${scale})`;
        });

        // Hysteresis: dock once fully scrolled past, but require scrolling
        // back up a bit further before undocking. A single hard cutoff at
        // ~0px flaps on/off with normal scroll jitter, which is what was
        // causing the jerk/flicker.
        if (!this.isDocked && logoWrapperHeight <= 0) {
          this.isDocked = true;
        } else if (this.isDocked && logoWrapperHeight > 24) {
          this.isDocked = false;
        }

        logoWrapper.classList.toggle(inactiveClass, this.isDocked);

        // Once the hero logo has fully scrolled out of view, tell the body
        // so the header's own (small) logo can fade in. The header CSS
        // already keys off this class:
        //   body:not(.hero-logo-docked) .header-logo-dock .header-logo { opacity: 0; }
        document.body.classList.toggle(this.dockedBodyClass, this.isDocked);

        // Flip the header transparent <-> solid at the exact same moment,
        // instead of a separate fixed scroll offset, so the dock crossfade
        // and the header background switch always happen together.
        this.updateHeaderState(this.isDocked);
      }

      updateHeaderState(isCollapsed) {
        const header = this.getHeader();
        if (!header) return;

        // Only this banner drives the transparent/solid switch when the
        // header actually has the transparent-homepage feature turned on.
        // Otherwise leave whatever server-rendered state (is-solid) alone.
        const transparentEnabled = header.dataset.transparentEnabled === "true";
        if (!transparentEnabled) return;

        header.classList.toggle("is-solid", isCollapsed);
        header.classList.toggle("is-transparent", !isCollapsed);
      }

      observeAndScaleElement() {
        this.setInitialScale();
        this.scrollListener = this.handleLogoSize.bind(this);
        document.addEventListener("scroll", this.scrollListener, { passive: true });
      }

      setReadyClass() {
        this.classList.add(this.readyClass);
      }

      init() {
        document.addEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent.bind(this),
        );

        this.setInitialScale();
        this.handleLogoSize();
        this.initializeVideoAndScaling();

        if (!this.video) {
          this.setReadyClass();
        }
      }

      initializeVideoAndScaling() {
        const isDesignMode = this.dataset.designMode;

        if (!this.video) {
          // No video block set (image-only banner) — go straight to
          // scroll-based scaling/docking. This is the common case, and
          // previously it was skipped entirely due to a bug below.
          this.observeAndScaleElement();
          this.handleLogoSize();
          this.setReadyClass();
          return;
        }

        this.video
          .play()
          .catch(() => {
            this.isLowBattery = true;
          })
          .finally(() => {
            if (this.isLowBattery) {
              this.classList.add(this.lowBatteryClass);
            }
            // Always observe/scale on scroll regardless of video state,
            // so the dock class still gets toggled even in low-battery mode.
            if (!this.scrollListener) {
              this.observeAndScaleElement();
            }

            this.handleLogoSize();
            this.setReadyClass();
          });

        if (isDesignMode && !this.scrollListener) {
          this.observeAndScaleElement();
        }
      }

      reinit() {
        setTimeout(this.handleLogoSize.bind(this), 0);
      }
    },
  );
}